import cloneDeep from "lodash.clonedeep";

import { AnalyticsProductDto } from "@intelligems/ig-types/dtos/analytics-products";
import {
  PutConfigureAnalyticsRequestBody,
  PutConfigureAnalyticsResponseBody,
} from "@intelligems/ig-types/functions/experiences/put-configure-analytics";
import { S3FileLocationDto } from "@intelligems/ig-types/dtos/s3-file-location";
import { useUploadProductsToS3 } from "@src/api/experiences/put-experience";
import { useDispatch } from "@src/store";
import { processSavedAnalyticsProducts } from "@src/store/slices/analyticsProducts";
import {
  initCustomMetrics,
  initKeyMetrics,
} from "@src/store/slices/experienceMetrics/experienceMetrics";
import { initExperienceAnalysis } from "@src/store/slices/experiences";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { api } from "../api";

type UploadProductsToS3Fn = ReturnType<
  typeof useUploadProductsToS3
>["mutateAsync"];

const putConfigureAnalytics = async ({
  _data,
  experienceId,
  allProductsLoaded,
  productsLocation,
  removeAllProducts,
  analyticsProducts,
  uploadProductsToS3,
}: {
  _data: PutConfigureAnalyticsRequestBody;
  experienceId: string;
  allProductsLoaded?: boolean;
  productsLocation?: S3FileLocationDto;
  removeAllProducts?: boolean;
  analyticsProducts: AnalyticsProductDto[];
  uploadProductsToS3: UploadProductsToS3Fn;
}) => {
  const data: PutConfigureAnalyticsRequestBody = cloneDeep(_data);
  if (productsLocation) {
    data.productsLocation = productsLocation;
  } else if (
    (allProductsLoaded && analyticsProducts?.length) ||
    removeAllProducts
  ) {
    data.productsLocation = await uploadProductsToS3({
      products: analyticsProducts,
      category: "experiment",
    });
  }
  const resp = await api.put<PutConfigureAnalyticsResponseBody>(
    `/v2/experiences/${experienceId}/configure-analytics`,
    data
  );
  return resp.data;
};

export const usePutConfigureAnalytics = ({
  experienceId,
  allProductsLoaded,
  productsLocation,
  removeAllProducts,
  analyticsProducts,
}: {
  experienceId: string;
  allProductsLoaded?: boolean;
  productsLocation?: S3FileLocationDto;
  removeAllProducts?: boolean;
  analyticsProducts: AnalyticsProductDto[];
}) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { mutateAsync: uploadProductsToS3 } = useUploadProductsToS3();
  return useMutation<
    PutConfigureAnalyticsResponseBody,
    Error,
    PutConfigureAnalyticsRequestBody
  >({
    mutationFn: async (data) =>
      putConfigureAnalytics({
        _data: data,
        experienceId,
        analyticsProducts,
        productsLocation,
        removeAllProducts,
        allProductsLoaded,
        uploadProductsToS3,
      }),
    mutationKey: ["put-configure-analytics"],
    onSuccess: (data: PutConfigureAnalyticsResponseBody) => {
      dispatch(
        initKeyMetrics({
          keyMetrics: data.experienceKeyMetrics,
        })
      );
      dispatch(
        initCustomMetrics({
          customMetrics: data.experienceCustomMetrics,
        })
      );

      dispatch(
        initExperienceAnalysis({
          experienceAnalysis: data.experienceAnalysis,
        })
      );

      dispatch(processSavedAnalyticsProducts());

      void queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey.some(
            (key) =>
              typeof key === "object" &&
              (JSON.stringify(key).includes("variation_") ||
                JSON.stringify(key).includes("experience_countries"))
          ),
      });
    },
  });
};
